import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import { environment } from "src/environments/environment";
import { TokenStorageService } from "src/app/services/token-storage.service";
import {
	FollowupAnswerDto,
	FollowupDto,
	HappinessResponseCreateDto,
	UxFeedbackEventDto,
	UxFeedbackService,
	UxFeedbackState,
} from "src/app/services/ux-feedback.service";

type FollowupQuestionType = "single_choice" | "multi_choice" | "text";

interface FollowupOption {
	key: string;
	text: string;
}

interface FollowupQuestionDefinition {
	orderNo: number;
	questionKey: string;
	questionText: string;
	questionType: FollowupQuestionType;
	required: boolean;
	options?: FollowupOption[];
}

@Component({
	selector: "app-happiness-meter",
	templateUrl: "./happiness-meter.component.html",
	styleUrls: ["./happiness-meter.component.scss"],
})
export class HappinessMeterComponent implements OnInit, OnDestroy {
	@ViewChild("followupInviteModal") followupInviteModal!: TemplateRef<any>;
	@ViewChild("followupQuestionModal") followupQuestionModal!: TemplateRef<any>;

	private readonly promptKey = "happiness_support";
	private readonly contextKey = "support_cta";
	private readonly clientSessionStorageKey = "uxFeedbackClientSessionId";
	private readonly appUserSessionStorageKey = "local-usuario-sesion";

	public questionText: string = "¿Cómo te sientes con nuestro servicio?";
	public states: UxFeedbackState[] = [
		{ value: 0, label: "Enojado" },
		{ value: 25, label: "Frustrado" },
		{ value: 50, label: "Neutral" },
		{ value: 75, label: "Contento" },
		{ value: 100, label: "Feliz" },
	];

	public selectedScore: number = 50;
	public selectedStateLabel: string = "Neutral";
	public hoveredScore: number | null = null;
	public isFabOpen: boolean = false;
	public widgetVisible: boolean = false;
	public eligible: boolean = true;
	public eligibilityLoading: boolean = true;
	public cooldownHours: number = 168;
	public promptVersion: number = 1;
	public nextAllowedAt?: Date;
	public sendingResponse: boolean = false;
	public followupSending: boolean = false;
	public followupQuestions: FollowupQuestionDefinition[] = [];
	public currentBucket: string = "";
	public formError: string = "";

	private shownAtMs: number = 0;
	private interactedSent: boolean = false;
	private responseId?: string;
	private inviteModalRef?: NgbModalRef;
	private questionsModalRef?: NgbModalRef;
	private cooldownRecheckTimerId: any = null;
	public singleAnswers: { [questionKey: string]: string } = {};
	public multiAnswers: { [questionKey: string]: string[] } = {};
	public textAnswers: { [questionKey: string]: string } = {};
	private clientSessionId: string = "";

	constructor(
		private uxFeedbackService: UxFeedbackService,
		private tokenStorageService: TokenStorageService,
		private router: Router,
		private modalService: NgbModal
	) {}

	ngOnInit(): void {
		this.clientSessionId = this.getOrCreateClientSessionId();
		this.syncSelectedState();
		this.loadEligibility();
	}

	ngOnDestroy(): void {
		this.clearCooldownRecheckTimer();
	}

	get disabledWidget(): boolean {
		return this.eligibilityLoading || this.sendingResponse || !this.eligible;
	}

	stateEmoji(value: number): string {
		switch (value) {
			case 0:
				return "😠";
			case 25:
				return "🙁";
			case 50:
				return "😐";
			case 75:
				return "🙂";
			case 100:
				return "😄";
			default:
				return "😐";
		}
	}

	stateToneClass(value: number): string {
		switch (value) {
			case 0:
				return "tone-angry";
			case 25:
				return "tone-frustrated";
			case 50:
				return "tone-neutral";
			case 75:
				return "tone-content";
			case 100:
				return "tone-happy";
			default:
				return "tone-neutral";
		}
	}

	get displayStateLabel(): string {
		if (this.hoveredScore === null) return this.selectedStateLabel;
		const hoveredState = this.states.find((x) => x.value === this.hoveredScore);
		return hoveredState ? hoveredState.label : this.selectedStateLabel;
	}

	get displayToneClass(): string {
		const score = this.hoveredScore === null ? this.selectedScore : this.hoveredScore;
		return this.stateToneClass(score);
	}

	onSliderChange(rawValue: string): void {
		const parsed = Number(rawValue);
		if (Number.isNaN(parsed)) return;

		this.selectedScore = parsed;
		this.syncSelectedState();
		this.sendInteractedIfNeeded();
	}

	onSliderRelease(rawValue: string): void {
		const parsed = Number(rawValue);
		if (Number.isNaN(parsed) || this.disabledWidget) return;

		this.selectedScore = parsed;
		this.syncSelectedState();
		this.confirmAndSubmitSelection();
	}

	onTickClick(value: number): void {
		if (this.disabledWidget) return;
		this.selectedScore = value;
		this.syncSelectedState();
		this.hoveredScore = null;
		this.sendInteractedIfNeeded();
		this.confirmAndSubmitSelection();
	}

	toggleFab(): void {
		if (this.disabledWidget) return;
		this.isFabOpen = !this.isFabOpen;
	}

	onTickHover(value: number): void {
		if (this.disabledWidget) return;
		this.hoveredScore = value;
	}

	onTickLeave(): void {
		this.hoveredScore = null;
	}

	submitResponse(): void {
		if (this.disabledWidget) return;

		this.sendingResponse = true;
		const timeToSubmitMs = Date.now() - this.shownAtMs;
		this.postEvent("SUBMITTED", { score: this.selectedScore });

		const dto: HappinessResponseCreateDto = {
			promptKey: this.promptKey,
			contextKey: this.contextKey,
			clientSessionId: this.clientSessionId,
			userSessionId: this.getCurrentUserSessionId(),
			emisorId: this.getCurrentEmisorId(),
			score: this.selectedScore,
			scoreBucket: this.selectedScore,
			stateLabel: this.selectedStateLabel,
			questionVersion: this.promptVersion,
			timeToSubmitMs,
			clientMetadataJson: JSON.stringify({
				pageRoute: this.getCurrentRoute(),
				userAgent: navigator.userAgent,
			}),
		};

		this.uxFeedbackService.postHappinessResponse(dto).subscribe({
			next: (response) => {
				if (response && response.status === 200 && response.data) {
					this.responseId = response.data;
					this.currentBucket = this.getBucketByScore(this.selectedScore);
					this.eligible = false;
					this.isFabOpen = false;
					this.nextAllowedAt = new Date(Date.now() + this.cooldownHours * 60 * 60 * 1000);
					this.openInviteModal();
				}
			},
			error: (error) => {
				const apiError = error?.error;
				const message = apiError?.errors?.length ? apiError.errors[0] : "No fue posible registrar tu respuesta.";
				const nextAllowedAt = apiError?.data?.nextAllowedAt;
				if (nextAllowedAt) {
					this.eligible = false;
					this.nextAllowedAt = new Date(nextAllowedAt);
				}

				Swal.fire({
					icon: "warning",
					title: "No disponible",
					text: message,
				});
				this.sendingResponse = false;
			},
			complete: () => {
				this.sendingResponse = false;
				this.loadEligibility();
			},
		});
	}

	acceptInvite(): void {
		if (this.inviteModalRef) {
			this.inviteModalRef.close();
		}

		this.prepareFollowupQuestions();
		this.postEvent("FOLLOWUP_STARTED", { bucket: this.currentBucket });
		this.questionsModalRef = this.modalService.open(this.followupQuestionModal, {
			windowClass: "ux-feedback-followup-modal",
			centered: true,
			backdrop: "static",
			keyboard: false,
		});
	}

	declineInvite(): void {
		if (this.inviteModalRef) {
			this.inviteModalRef.close();
		}
		this.postEvent("DISMISSED", { from: "invite_modal" });
	}

	cancelFollowup(): void {
		if (this.questionsModalRef) {
			this.questionsModalRef.close();
		}
		this.postEvent("DISMISSED", { from: "followup_modal" });
	}

	onSingleChoice(questionKey: string, optionKey: string): void {
		this.singleAnswers[questionKey] = optionKey;
	}

	onMultiChoice(questionKey: string, optionKey: string, checked: boolean): void {
		const selected = this.multiAnswers[questionKey] || [];
		if (checked) {
			if (selected.indexOf(optionKey) < 0) selected.push(optionKey);
		} else {
			this.multiAnswers[questionKey] = selected.filter((key) => key !== optionKey);
			return;
		}
		this.multiAnswers[questionKey] = selected;
	}

	isMultiSelected(questionKey: string, optionKey: string): boolean {
		return (this.multiAnswers[questionKey] || []).indexOf(optionKey) >= 0;
	}

	onTextAnswerChanged(questionKey: string, value: string): void {
		this.textAnswers[questionKey] = value ? value.substring(0, 500) : "";
	}

	getTextLength(questionKey: string): number {
		return (this.textAnswers[questionKey] || "").length;
	}

	submitFollowup(): void {
		if (!this.responseId || !this.currentBucket) return;
		if (this.followupSending) return;
		if (!this.validateFollowup()) return;

		const payload = this.buildFollowupPayload();
		this.followupSending = true;

		this.uxFeedbackService.postFollowupAnswers(this.responseId, payload).subscribe({
			next: (response) => {
				if (response && response.status === 200) {
					this.postEvent("FOLLOWUP_COMPLETED", { bucket: this.currentBucket });
					if (this.questionsModalRef) {
						this.questionsModalRef.close();
					}
					Swal.fire({
						icon: "success",
						title: "Gracias por tu feedback",
						text: "Tus respuestas fueron registradas correctamente.",
					});
				}
			},
			error: (error) => {
				const message = error?.error?.errors?.length ? error.error.errors[0] : "No fue posible registrar el followup.";
				Swal.fire({
					icon: "error",
					title: "Error",
					text: message,
				});
				this.followupSending = false;
			},
			complete: () => {
				this.followupSending = false;
			},
		});
	}

	private loadEligibility(): void {
		this.eligibilityLoading = true;
		this.clearCooldownRecheckTimer();

		this.uxFeedbackService.getEligibility(this.contextKey, this.getCurrentRoute(), this.promptKey).subscribe({
			next: (response) => {
				if (response && response.status === 200 && response.data) {
					this.eligible = response.data.eligible;
					this.cooldownHours = response.data.cooldownHours;
					this.promptVersion = response.data.promptVersion;
					this.nextAllowedAt = response.data.nextAllowedAt ? new Date(response.data.nextAllowedAt) : undefined;
					if (response.data.states && response.data.states.length > 0) {
						this.states = response.data.states;
						if (!this.states.some((x) => x.value === this.selectedScore)) {
							this.selectedScore = this.states[0].value;
						}
						this.syncSelectedState();
					}
				}

				this.refreshWidgetVisibility();
			},
			error: () => {
				this.eligible = true;
				if (!this.widgetVisible) {
					this.widgetVisible = true;
					this.shownAtMs = Date.now();
					this.interactedSent = false;
					this.postEvent("SHOWN");
				}
			},
			complete: () => {
				this.eligibilityLoading = false;
			},
		});
	}

	private sendInteractedIfNeeded(): void {
		if (this.interactedSent) return;
		this.interactedSent = true;
		this.postEvent("INTERACTED", { score: this.selectedScore });
	}

	private postEvent(eventType: string, metadata?: any): void {
		const eventDto: UxFeedbackEventDto = {
			promptKey: this.promptKey,
			contextKey: this.contextKey,
			eventType,
			clientSessionId: this.clientSessionId,
			userSessionId: this.getCurrentUserSessionId(),
			emisorId: this.getCurrentEmisorId(),
			pageRoute: this.getCurrentRoute(),
			appVersion: environment.appVersion,
			userAgent: navigator.userAgent,
			metadataJson: metadata ? JSON.stringify(metadata) : undefined,
			occurredAt: new Date().toISOString(),
			timeSinceShownMs: Date.now() - this.shownAtMs,
		};

		this.uxFeedbackService.postEvent(eventDto).subscribe({
			error: () => {
				// No bloquea flujo de UX
			},
		});
	}

	private syncSelectedState(): void {
		const state = this.states.find((x) => x.value === this.selectedScore);
		this.selectedStateLabel = state ? state.label : "";
	}

	private refreshWidgetVisibility(): void {
		const wasVisible = this.widgetVisible;

		if (this.eligible) {
			this.widgetVisible = true;
			if (!wasVisible) {
				this.shownAtMs = Date.now();
				this.interactedSent = false;
				this.postEvent("SHOWN");
			}
			return;
		}

		this.widgetVisible = false;
		this.isFabOpen = false;
		if (!this.nextAllowedAt) return;

		const msUntilNextAllowed = this.nextAllowedAt.getTime() - Date.now();
		if (msUntilNextAllowed <= 0) {
			this.widgetVisible = true;
			return;
		}

		this.cooldownRecheckTimerId = setTimeout(() => {
			this.loadEligibility();
		}, msUntilNextAllowed + 500);
	}

	private clearCooldownRecheckTimer(): void {
		if (!this.cooldownRecheckTimerId) return;
		clearTimeout(this.cooldownRecheckTimerId);
		this.cooldownRecheckTimerId = null;
	}

	private confirmAndSubmitSelection(): void {
		if (this.sendingResponse || !this.eligible) return;

		Swal.fire({
			icon: "question",
			title: "Confirmar respuesta",
			text: `¿Quieres enviar "${this.selectedStateLabel}"?`,
			showCancelButton: true,
			confirmButtonText: "Sí, enviar",
			cancelButtonText: "Cancelar",
		}).then((result) => {
			if (result.isConfirmed) {
				this.submitResponse();
			}
		});
	}

	private openInviteModal(): void {
		this.inviteModalRef = this.modalService.open(this.followupInviteModal, {
			size: "sm",
			windowClass: "ux-feedback-invite-modal",
			centered: true,
			backdrop: "static",
			keyboard: false,
		});
	}

	private prepareFollowupQuestions(): void {
		this.formError = "";
		this.singleAnswers = {};
		this.multiAnswers = {};
		this.textAnswers = {};
		this.followupQuestions = this.getQuestionsByBucket(this.currentBucket);
	}

	private getQuestionsByBucket(bucket: string): FollowupQuestionDefinition[] {
		if (bucket === "low") {
			return [
				{
					orderNo: 1,
					questionKey: "LOW_MAIN_REASON",
					questionText: "¿Qué fue lo principal que te molestó hoy?",
					questionType: "single_choice",
					required: true,
					options: [
						{ key: "BUG", text: "Error o bug" },
						{ key: "DTE_NO_ESPERADO", text: "DTE no esperado" },
						{ key: "LENTITUD", text: "Lentitud" },
						{ key: "DIFICIL_ENCONTRAR", text: "Difícil encontrar opciones" },
						{ key: "PAGOS_PLAN", text: "Pagos / plan" },
						{ key: "SOPORTE_NO_RESOLVIO", text: "Soporte no resolvió" },
						{ key: "OTRO", text: "Otro" },
					],
				},
				{
					orderNo: 2,
					questionKey: "LOW_MODULE",
					questionText: "¿En qué parte ocurrió?",
					questionType: "multi_choice",
					required: true,
					options: [
						{ key: "EMISION", text: "Emisión" },
						{ key: "RECIBIDOS", text: "Documentos recibidos" },
						{ key: "CLIENTES", text: "Clientes" },
						{ key: "PRODUCTOS", text: "Productos" },
						{ key: "REPORTES", text: "Reportes" },
						{ key: "INTEGRACIONES", text: "Integraciones" },
						{ key: "SOPORTE", text: "Soporte" },
						{ key: "OTRO", text: "Otro" },
					],
				},
				{
					orderNo: 3,
					questionKey: "LOW_DETAIL",
					questionText: "Cuéntanos qué pasó (opcional).",
					questionType: "text",
					required: false,
				},
			];
		}

		if (bucket === "mid") {
			return [
				{
					orderNo: 1,
					questionKey: "MID_IMPROVEMENT",
					questionText: "¿Qué haría que tu experiencia pase de neutral a contento?",
					questionType: "single_choice",
					required: true,
					options: [
						{ key: "CLARIDAD_AYUDA", text: "Mayor claridad en ayuda" },
						{ key: "MENOS_PASOS", text: "Menos pasos" },
						{ key: "MAS_RAPIDEZ", text: "Más rapidez" },
						{ key: "MEJOR_SOPORTE", text: "Mejor soporte" },
						{ key: "MAS_FUNCIONES", text: "Más funciones" },
						{ key: "OTRO", text: "Otro" },
					],
				},
				{
					orderNo: 2,
					questionKey: "MID_TODAY",
					questionText: "Hoy fue…",
					questionType: "single_choice",
					required: true,
					options: [
						{ key: "MAS_DIFICIL", text: "Más difícil" },
						{ key: "COMO_ESPERABA", text: "Como esperaba" },
						{ key: "MAS_FACIL", text: "Más fácil" },
					],
				},
				{
					orderNo: 3,
					questionKey: "MID_SUGGESTION",
					questionText: "1 sugerencia concreta (opcional).",
					questionType: "text",
					required: false,
				},
			];
		}

		return [
			{
				orderNo: 1,
				questionKey: "HIGH_LIKE",
				questionText: "¿Qué es lo que más te gusta?",
				questionType: "multi_choice",
				required: true,
				options: [
					{ key: "RAPIDEZ", text: "Rapidez" },
					{ key: "FACILIDAD", text: "Facilidad de uso" },
					{ key: "SOPORTE", text: "Soporte" },
					{ key: "ORDEN_CUMPLIMIENTO", text: "Orden y cumplimiento" },
					{ key: "INTEGRACIONES", text: "Integraciones" },
					{ key: "PRECIO", text: "Precio" },
					{ key: "OTRO", text: "Otro" },
				],
			},
			{
				orderNo: 2,
				questionKey: "HIGH_IMPROVEMENT",
				questionText: "Si tuvieras que elegir una mejora, ¿cuál sería?",
				questionType: "single_choice",
				required: true,
				options: [
					{ key: "CLARIDAD_AYUDA", text: "Mayor claridad en ayuda" },
					{ key: "MENOS_PASOS", text: "Menos pasos" },
					{ key: "MAS_RAPIDEZ", text: "Más rapidez" },
					{ key: "MEJOR_SOPORTE", text: "Mejor soporte" },
					{ key: "MAS_FUNCIONES", text: "Más funciones" },
					{ key: "OTRO", text: "Otro" },
				],
			},
			{
				orderNo: 3,
				questionKey: "HIGH_HIGHLIGHT",
				questionText: "¿Algo que quieras destacar? (opcional).",
				questionType: "text",
				required: false,
			},
		];
	}

	private validateFollowup(): boolean {
		this.formError = "";

		for (let i = 0; i < this.followupQuestions.length; i++) {
			const question = this.followupQuestions[i];
			if (question.required) {
				if (question.questionType === "single_choice" && !this.singleAnswers[question.questionKey]) {
					this.formError = "Debes responder las preguntas obligatorias.";
					return false;
				}
				if (question.questionType === "multi_choice" && !(this.multiAnswers[question.questionKey] || []).length) {
					this.formError = "Debes responder las preguntas obligatorias.";
					return false;
				}
			}

			if (question.questionType === "text" && (this.textAnswers[question.questionKey] || "").length > 500) {
				this.formError = "Cada respuesta de texto permite máximo 500 caracteres.";
				return false;
			}
		}

		return true;
	}

	private buildFollowupPayload(): FollowupDto {
		const answers: FollowupAnswerDto[] = [];

		for (let i = 0; i < this.followupQuestions.length; i++) {
			const question = this.followupQuestions[i];

			if (question.questionType === "single_choice") {
				const selectedOptionKey = this.singleAnswers[question.questionKey];
				if (!selectedOptionKey) continue;

				const selectedOption = (question.options || []).find((x) => x.key === selectedOptionKey);
				answers.push({
					orderNo: question.orderNo,
					questionKey: question.questionKey,
					questionText: question.questionText,
					questionType: question.questionType,
					optionKey: selectedOptionKey,
					optionText: selectedOption ? selectedOption.text : selectedOptionKey,
				});
			}

			if (question.questionType === "multi_choice") {
				const selectedOptionKeys = this.multiAnswers[question.questionKey] || [];
				for (let j = 0; j < selectedOptionKeys.length; j++) {
					const optionKey = selectedOptionKeys[j];
					const selectedOption = (question.options || []).find((x) => x.key === optionKey);
					answers.push({
						orderNo: question.orderNo,
						questionKey: question.questionKey,
						questionText: question.questionText,
						questionType: question.questionType,
						optionKey: optionKey,
						optionText: selectedOption ? selectedOption.text : optionKey,
					});
				}
			}

			if (question.questionType === "text") {
				const answerText = (this.textAnswers[question.questionKey] || "").trim();
				if (!answerText) continue;

				answers.push({
					orderNo: question.orderNo,
					questionKey: question.questionKey,
					questionText: question.questionText,
					questionType: question.questionType,
					answerText: answerText,
				});
			}
		}

		return {
			bucket: this.currentBucket,
			answers,
		};
	}

	private getCurrentRoute(): string {
		return this.router.url || "/";
	}

	private getCurrentEmisorId(): string | undefined {
		const company = this.tokenStorageService.getCompany();
		return company ? company : undefined;
	}

	private getCurrentUserSessionId(): string | undefined {
		const raw = sessionStorage.getItem(this.appUserSessionStorageKey);
		if (!raw) return undefined;
		try {
			const parsed = JSON.parse(raw);
			return parsed && parsed.id ? parsed.id : undefined;
		} catch {
			return undefined;
		}
	}

	private getOrCreateClientSessionId(): string {
		const existing = sessionStorage.getItem(this.clientSessionStorageKey);
		if (existing) return existing;
		const generated = this.generateGuid();
		sessionStorage.setItem(this.clientSessionStorageKey, generated);
		return generated;
	}

	private getBucketByScore(score: number): string {
		if (score >= 75) return "high";
		if (score === 50) return "mid";
		return "low";
	}

	private generateGuid(): string {
		const cryptoApi = window.crypto as Crypto;
		const randomUuidFn = (cryptoApi as any).randomUUID;
		if (cryptoApi && typeof randomUuidFn === "function") {
			return randomUuidFn.call(cryptoApi);
		}

		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
			const random = (Math.random() * 16) | 0;
			const value = char === "x" ? random : (random & 0x3) | 0x8;
			return value.toString(16);
		});
	}
}
